import Api from '../../services/Api';

export default {
  namespaced: true,
  state: {
    area: 0,
    centroide: null,
    currentGeoFeature: {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [],
          },
        },
      ],
    },
    featureInfoSigpac: {},
    sigpacIntersect: {},
    dissolveFeatures: {},
    sigpacMap: '',
    sigpacWFS: '',
  },
  getters: {
    viewCentroide(state) {
      if (state.centroide === null) {
        return { lat: 0, lng: 0 };
      }
      return state.centroide;
    },
  },
  actions: {
    setLocation({ commit }, {
      newCoordinates,
      index,
      area,
      centroide,
    }) {
      commit('setArea', area);
      commit('setCentroide', centroide);
      commit('setCurrentGeoFeature', { newCoordinates, index });
    },
    setCurrentGeoFeature(context, { newCoordinates, index }) {
      context.commit('setCurrentGeoFeature', { newCoordinates, index });
    },
    resetCurrentGeoFeature({ commit }) {
      commit('resetCurrentGeoFeature');
    },
    resetAll({ commit }) {
      commit('resetCurrentGeoFeature');
      commit('resetArea');
      commit('resetCentroide');
    },
    resetCentroide({ commit }) {
      commit('resetCentroide');
      commit('resetArea');
    },
    // NOTA: acciones sin caller activo en la app a fecha de esta migración (verificado, ningún
    // componente hace dispatch de 'GisEditor/*'). Se ramifican igualmente por gis_version por
    // consistencia, para que si se retoman en el futuro ya respeten el flag.
    async fetchFeatureInfoSigpac({ commit, rootGetters }, { url, companyId }) {
      try {
        let requestOptions;
        if (rootGetters['Settings/isGisV2']) {
          await Api.renewAccessToken();
          requestOptions = {
            method: 'GET',
            redirect: 'follow',
            headers: {
              Authorization: Api.getHeaderAuth(),
              company: companyId,
            },
          };
        } else {
          requestOptions = { method: 'GET', redirect: 'follow' };
        }

        await fetch(url, requestOptions)
          .then((response) => response.json())
          .then((result) => {
            commit('setFeatureInfoSigpac', result);
          })
          .catch((error) => Promise.reject(error));
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error); // Esta llamada no tiene los mensajes de error normalizados
      }
    },
    // Confirmado contra GIS real (pre): POST {"polygon": "<coords planas>"} devuelve el mismo
    // FeatureCollection que el legacy GET /gisw/ws/getsuperficie/?polygon=<coords planas>.
    async fetchSigpacIntersect({ commit, rootGetters }, { layer, companyId }) {
      try {
        let url;
        let requestOptions;
        if (rootGetters['Settings/isGisV2']) {
          await Api.renewAccessToken();
          url = `${Api.getBaseUrl()}/gis/v2/geometric_surface`;
          requestOptions = {
            method: 'POST',
            redirect: 'follow',
            headers: {
              'Content-Type': 'application/json',
              Authorization: Api.getHeaderAuth(),
              company: companyId,
            },
            body: JSON.stringify({ polygon: layer }),
          };
        } else {
          url = `${Api.getGeoServerSigpacSurfaceLegacy()}?polygon=${layer}`;
          requestOptions = { method: 'GET', redirect: 'follow' };
        }
        await fetch(url, requestOptions)
          .then((response) => response.text())
          .then((result) => {
            commit('setSigpacIntersect', JSON.parse(result));
          })
          .catch((error) => Promise.reject(error));
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    // Confirmado contra GIS real (pre): POST {"feature": <FeatureCollection>} (el campo se llama
    // "feature" en singular pero acepta la FeatureCollection completa) devuelve el mismo Feature
    // disuelto que el legacy GET /gisw/ws/get_dissolve/?fc=<FeatureCollection>.
    async fetchDissolveFeatures({ commit, rootGetters }, { featureCollection, companyId }) {
      try {
        let url;
        let requestOptions;
        if (rootGetters['Settings/isGisV2']) {
          await Api.renewAccessToken();
          url = `${Api.getBaseUrl()}/gis/v2/dissolve_geojson_features`;
          requestOptions = {
            method: 'POST',
            redirect: 'follow',
            headers: {
              'Content-Type': 'application/json',
              Authorization: Api.getHeaderAuth(),
              company: companyId,
            },
            body: JSON.stringify({ feature: featureCollection }),
          };
        } else {
          url = `${Api.getGeoServerDissolveLegacy()}?fc=${featureCollection}`;
          requestOptions = { method: 'GET', redirect: 'follow' };
        }
        await fetch(url, requestOptions)
          .then((response) => response.text())
          .then((result) => {
            commit('setDissolveFeatures', JSON.parse(result));
          })
          .catch((error) => Promise.reject(error));
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async fetchSigpacMap({ commit, rootGetters }, { url, companyId }) {
      try {
        let requestOptions;
        if (rootGetters['Settings/isGisV2']) {
          await Api.renewAccessToken();
          requestOptions = {
            method: 'GET',
            redirect: 'follow',
            headers: {
              Authorization: Api.getHeaderAuth(),
              company: companyId,
            },
          };
        } else {
          requestOptions = { method: 'GET', redirect: 'follow' };
        }

        await fetch(url, requestOptions)
          .then((response) => {
            commit('setSigpacMap', response);
          })
          .catch((error) => Promise.reject(error));
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error); // Esta llamada no tiene los mensajes de error normalizados
      }
    },
    // Confirmado contra GIS real (pre): mismo GET, solo cambian los nombres de los params
    // (LowerCorner/UpperCorner del legacy pasan a lower_corner/upper_corner en snake_case).
    async fetchSigpacWFS({ commit, rootGetters }, parameters) {
      try {
        let url;
        let requestOptions;
        if (rootGetters['Settings/isGisV2']) {
          await Api.renewAccessToken();
          url = `${Api.getBaseUrl()}/gis/v2/sigpac_geometry?lower_corner=${parameters.LowerCorner}&upper_corner=${parameters.UpperCorner}`;
          requestOptions = {
            method: 'GET',
            redirect: 'follow',
            headers: {
              Authorization: Api.getHeaderAuth(),
              company: parameters.companyId,
            },
          };
        } else {
          url = `${Api.getGeoServerSigpacWFSGeom()}?LowerCorner=${parameters.LowerCorner}&UpperCorner=${parameters.UpperCorner}`;
          requestOptions = { method: 'GET', redirect: 'follow' };
        }
        await fetch(url, requestOptions)
          .then((response) => response.text())
          .then((data) => JSON.parse(data))
          .then((geodata) => {
            commit('setSigpacWFS', geodata);
          })
          .catch((error) => Promise.reject(error));
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error); // Esta llamada no tiene los mensajes de error normalizados
      }
    },
    setCentroide({ commit }, centroide) {
      commit('setCentroide', centroide);
    },
    setArea({ commit }, area) {
      commit('setArea', area);
    },
    setCurrentGeoFeatureRaw(context, geoJson) {
      context.commit('setCurrentGeoFeatureRaw', geoJson);
    },
  },
  mutations: {
    setArea(state, newArea) {
      state.area = newArea;
    },
    setCentroide(state, newCentroide) {
      state.centroide = newCentroide;
    },
    setCurrentGeoFeature(state, { newCoordinates, index }) {
      state.currentGeoFeature.features[index].geometry.coordinates = newCoordinates;
    },
    resetCurrentGeoFeature(state) {
      state.currentGeoFeature = {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'Polygon',
              coordinates: [],
            },
          },
        ],
      };
    },
    resetArea(state) {
      state.area = 0;
    },
    resetCentroide(state) {
      state.centroide = null;
    },
    setFeatureInfoSigpac(state, newFeatureInfoSigpac) {
      state.featureInfoSigpac = newFeatureInfoSigpac;
    },
    setSigpacIntersect(state, newSigpacIntersect) {
      state.sigpacIntersect = newSigpacIntersect;
    },
    setDissolveFeatures(state, newDissolveFeatures) {
      state.dissolveFeatures = newDissolveFeatures;
    },
    setSigpacMap(state, newSigpacMap) {
      state.sigpacMap = newSigpacMap;
    },
    setSigpacWFS(state, newSigpacWFS) {
      state.sigpacWFS = newSigpacWFS;
    },
    setCurrentGeoFeatureRaw(state, geoJSON) {
      state.currentGeoFeature = geoJSON;
    },
  },
};
